import { getPickerIcon } from './Icons/utils';
import { DEFAULT_GROUP_LIST, PickerIconsEnum, GroupListType } from './constants';
import { BaseProps } from '#components';
import { NoneIcon, TriangleDownIcon } from '#icons';
import { Box, Button, IconButton, Paper, Popover, Stack, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useMemo, useRef, useState } from 'react';

const TriggerButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  borderRadius: '16px',
  padding: `5px ${theme.spacing(2.5)}`,
  gap: theme.spacing(1),
}));

const useSxProps = () => {
  const theme = useTheme();
  return useMemo(() => {
    return {
      root: {
        width: '360px',
        padding: theme.spacing(1),
      },
      itemList: {
        margin: theme.spacing(0.5),
      },
    };
  }, [theme]);
};

interface IconPicker {
  onChange: (value: PickerIconsEnum) => void;
  selectedIcon?: string;
  list?: GroupListType;
  /**
   * `'icon'` (default) renders the selected icon alone.
   * `'button'` renders a contained-secondary pill with icon + chevron — used in
   * dialogs where the picker needs to read as a button.
   */
  variant?: 'icon' | 'button';
}

export const IconPicker: FC<IconPicker & BaseProps> = ({
  dataTestId,
  selectedIcon,
  onChange,
  list = DEFAULT_GROUP_LIST,
  variant = 'icon',
}) => {
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const sxProps = useSxProps();

  const handleOpenClose = () => {
    setOpen(!open);
  };

  const triggerA11yProps = {
    'aria-label': 'Select icon',
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
  } as const;

  const trigger =
    variant === 'button' ? (
      <TriggerButton
        variant="contained"
        color="secondary"
        ref={buttonRef}
        onClick={handleOpenClose}
        {...triggerA11yProps}
      >
        {selectedIcon ? (getPickerIcon(selectedIcon) ?? <NoneIcon color="action" />) : <NoneIcon color="action" />}
        <TriangleDownIcon fontSize="small" />
      </TriggerButton>
    ) : (
      <IconButton onClick={handleOpenClose} ref={buttonRef} {...triggerA11yProps}>
        {selectedIcon ? (getPickerIcon(selectedIcon) ?? <NoneIcon color="action" />) : <NoneIcon color="action" />}
      </IconButton>
    );

  return (
    <Box data-testid={dataTestId}>
      {trigger}
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        disableScrollLock
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {/* eslint-disable-next-line no-restricted-syntax */}
        <Paper sx={sxProps.root}>
          {list.map((group) => {
            return (
              <Stack key={group.name} direction="column">
                <Box ml={1.5} mt={1.5}>
                  <Typography color="text.secondary" variant="h5" fontWeight={600}>
                    {group.name}
                  </Typography>
                </Box>
                <Stack direction="row" flexWrap="wrap">
                  {group.icons.map((icon) => {
                    return (
                      <IconButton
                        // The computed style gives each icon its picker layout and selected state.
                        // eslint-disable-next-line no-restricted-syntax
                        sx={sxProps.itemList}
                        key={icon}
                        onClick={() => {
                          setOpen(false);
                          onChange(icon);
                        }}
                        onSelect={() => {
                          setOpen(false);
                          onChange(icon);
                        }}
                      >
                        {getPickerIcon(icon)}
                      </IconButton>
                    );
                  })}
                </Stack>
              </Stack>
            );
          })}
        </Paper>
      </Popover>
    </Box>
  );
};
